import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils';
import { API_URL } from '../constants';

function parseDate(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function areaOf(conv) {
  return conv?.categoria || conv?.area || 'sin_area';
}

function isClosed(conv) {
  return conv?.estado === 'cerrada' || conv?.estado === 'cerrado';
}

function isAiClosed(conv) {
  const cerradoPor = String(conv?.cerrada_por || conv?.cerrado_por || '').toLowerCase();
  const motivo = String(conv?.motivo_cierre || '').toLowerCase();
  return isClosed(conv) && (cerradoPor === 'ia' || cerradoPor === 'ai' || motivo.includes('ia'));
}

function getAgentId(conv) {
  return conv?.agente_id || conv?.agenteId || conv?.asignado_a || conv?.usuario_id || null;
}

function getAgentName(agentId, agentes = []) {
  if (!agentId) return 'Sin asignar';
  const found = agentes.find((a) => String(a.id) === String(agentId));
  return found?.nombre || found?.name || found?.email || `Agente ${String(agentId).slice(0, 6)}`;
}

function periodStart(periodo) {
  const now = new Date();
  if (periodo === 'diario') return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (periodo === 'semanal') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function isWithin(conv, periodo) {
  const d = parseDate(conv?.updated_at || conv?.created_at || conv?.cerrada_at);
  if (!d) return true;
  return d >= periodStart(periodo);
}

function statusTagClass(value) {
  if (value === 'alto' || value === 'vencido') return 'ro-tag red';
  if (value === 'medio' || value === 'pendiente') return 'ro-tag amber';
  if (value === 'ok' || value === 'resuelto') return 'ro-tag green';
  return 'ro-tag blue';
}

function Bar({ label, value, max, tone = 'blue' }) {
  const pct = max > 0 ? Math.max(4, Math.min(100, Math.round((value / max) * 100))) : 0;
  return (
    <div className="ro-bar-row">
      <div className="ro-bar-label">{label}</div>
      <div className="ro-bar-track"><div className={`ro-bar-fill ${tone}`} style={{ width: `${pct}%` }} /></div>
      <div className="ro-bar-value">{value}</div>
    </div>
  );
}

export default function ReporteOperativo({
  conversaciones = [],
  agentes = [],
  ventas,
  atencion,
  soporte,
  areasHabilitadas = [],
  onIrAConversacion,
}) {
  const [periodo, setPeriodo] = useState('diario');
  const [areaFiltro, setAreaFiltro] = useState('todas');
  const [metricasBackend, setMetricasBackend] = useState([]);
  const [alertasIA, setAlertasIA] = useState([]);

  // Cargar métricas del backend
  const cargarMetricas = useCallback(async () => {
    try {
      const fechaHoy = new Date().toISOString().split('T')[0];
      const resp = await authFetch(`${API_URL}/api/dashboard/metricas?fecha=${fechaHoy}`);
      if (resp.ok) {
        const json = await resp.json();
        setMetricasBackend(json.metricas || []);
      }
    } catch (err) {
      console.error('[REPORTE] Error cargando métricas:', err.message);
    }
  }, []);

  // Cargar alertas del auditor IA
  const cargarAlertasIA = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_URL}/api/auditor/alertas?estado=pendiente&limit=50`);
      if (resp.ok) {
        const json = await resp.json();
        setAlertasIA(json.alertas || []);
      }
    } catch (err) {
      console.error('[REPORTE] Error cargando alertas IA:', err.message);
    }
  }, []);

  // Polling: recargar métricas y alertas cada 30 segundos
  useEffect(() => {
    cargarMetricas();
    cargarAlertasIA();
    const interval = setInterval(() => {
      cargarMetricas();
      cargarAlertasIA();
    }, 30000);
    return () => clearInterval(interval);
  }, [cargarMetricas, cargarAlertasIA]);

  const data = useMemo(() => {
    const base = conversaciones
      .filter((c) => isWithin(c, periodo))
      .filter((c) => areaFiltro === 'todas' || areaOf(c) === areaFiltro)
      .filter((c) => areaFiltro === 'todas' ? true : areasHabilitadas.includes(areaFiltro));

    const now = Date.now();
    const abiertas = base.filter((c) => !isClosed(c));
    const cerradas = base.filter(isClosed);
    const iaCerradas = base.filter(isAiClosed);
    const humanoCerradas = cerradas.filter((c) => !isAiClosed(c));
    const humanoSinCerrar = abiertas.filter((c) => getAgentId(c) && c.ia_activa === false);
    const sinAsignar = abiertas.filter((c) => !getAgentId(c));

    const reloj = humanoSinCerrar.map((c) => {
      const refDate = parseDate(c.updated_at || c.created_at) || new Date();
      const ms = now - refDate.getTime();
      const area = areaOf(c);
      let accion = c.proxima_accion || c.proximaAccion || '';
      if (!accion && area === 'ventas') accion = ventas?.obtenerVentaLocal?.(c)?.proximaAccion || 'Revisar lead';
      if (!accion && area === 'atencion') accion = atencion?.obtenerAtencionLocal?.(c)?.proximaAccion || 'Revisar cuenta';
      if (!accion && area === 'soporte') accion = soporte?.obtenerSoporteLocal?.(c)?.proximaAccion || 'Revisar diagnostico';
      if (!accion) accion = 'Definir proxima accion';
      return { conv: c, ms, area, accion, agente: getAgentName(getAgentId(c), agentes) };
    }).sort((a, b) => b.ms - a.ms);

    const byArea = ['ventas', 'atencion', 'soporte'].map((area) => ({
      area,
      abiertas: abiertas.filter((c) => areaOf(c) === area).length,
      cerradas: cerradas.filter((c) => areaOf(c) === area).length,
      ia: iaCerradas.filter((c) => areaOf(c) === area).length,
    }));

    const agentMap = new Map();
    agentes.forEach((a) => {
      // Buscar métricas del backend para este agente
      const metricaBE = metricasBackend.find((m) => String(m.agente_id) === String(a.id));

      agentMap.set(String(a.id), {
        id: a.id,
        nombre: a.nombre || a.name || a.email || `Agente ${String(a.id).slice(0, 6)}`,
        tomadas: metricaBE?.conversaciones_tomadas || 0,
        cerradas: metricaBE?.conversaciones_cerradas || 0,
        transferidas: metricaBE?.conversaciones_transferidas || 0,
        cerradasSinRespuesta: metricaBE?.conversaciones_cerradas_sin_respuesta || 0,
        tiempoOnlineMin: metricaBE?.tiempo_total_online || 0,
        abiertas: 0,
        relojMs: 0,
        iaCerradas: 0,
        online: a.online || false,
        ultimaActividad: a.ultima_actividad || null,
      });
    });

    base.forEach((c) => {
      const id = getAgentId(c);
      if (!id) return;
      const key = String(id);
      if (!agentMap.has(key)) agentMap.set(key, {
        id,
        nombre: getAgentName(id, agentes),
        tomadas: 0,
        cerradas: 0,
        transferidas: 0,
        cerradasSinRespuesta: 0,
        tiempoOnlineMin: 0,
        abiertas: 0,
        relojMs: 0,
        iaCerradas: 0,
        online: false,
        ultimaActividad: null,
      });
      const item = agentMap.get(key);
      // Solo contar abiertas y reloj desde conversaciones en memoria
      // tomadas y cerradas vienen del backend (más precisas)
      if (!isClosed(c)) {
        item.abiertas += 1;
        const refDate = parseDate(c.updated_at || c.created_at) || new Date();
        item.relojMs += Math.max(0, now - refDate.getTime());
      }
      if (isAiClosed(c)) item.iaCerradas += 1;
    });

    const agentesReporte = [...agentMap.values()].sort((a, b) => b.abiertas - a.abiertas || b.tomadas - a.tomadas);

    const maxArea = Math.max(1, ...byArea.map((x) => x.abiertas));
    const maxAgentOpen = Math.max(1, ...agentesReporte.map((x) => x.abiertas));

    return { base, abiertas, cerradas, iaCerradas, humanoCerradas, humanoSinCerrar, sinAsignar, reloj, byArea, agentesReporte, maxArea, maxAgentOpen };
  }, [conversaciones, agentes, periodo, areaFiltro, areasHabilitadas, ventas, atencion, soporte, metricasBackend]);

  const periodoLabel = periodo === 'diario' ? 'Hoy' : periodo === 'semanal' ? 'Ultimos 7 dias' : 'Mes actual';

  return (
    <section className="report-page">
      <div className="report-top">
        <div>
          <h1>Reporte operativo CRM</h1>
          <p>Control ejecutivo de conversaciones abiertas, cierres IA/humano, agentes y backlog.</p>
        </div>
        <div className="report-filters">
          <button className={periodo === 'diario' ? 'active' : ''} onClick={() => setPeriodo('diario')}>Diario</button>
          <button className={periodo === 'semanal' ? 'active' : ''} onClick={() => setPeriodo('semanal')}>Semanal</button>
          <button className={periodo === 'mensual' ? 'active' : ''} onClick={() => setPeriodo('mensual')}>Mensual</button>
          <select value={areaFiltro} onChange={(e) => setAreaFiltro(e.target.value)}>
            <option value="todas">Todas las areas</option>
            <option value="ventas">Ventas</option>
            <option value="atencion">Atencion</option>
            <option value="soporte">Soporte</option>
          </select>
        </div>
      </div>

      <div className="report-kpis">
        <div className="ro-card ro-kpi"><span>Periodo</span><strong>{periodoLabel}</strong><small>{data.base.length} conversaciones analizadas</small></div>
        <div className="ro-card ro-kpi"><span>Abiertas</span><strong className="warn">{data.abiertas.length}</strong><small>{data.sinAsignar.length} sin asignar</small></div>
        <div className="ro-card ro-kpi"><span>Humano sin cerrar</span><strong className="bad">{data.humanoSinCerrar.length}</strong><small>Reloj corriendo luego de intervencion</small></div>
        <div className="ro-card ro-kpi"><span>Cierres IA</span><strong className="good">{data.iaCerradas.length}</strong><small>{data.humanoCerradas.length} cierres humanos</small></div>
      </div>

      <div className="report-note">
        Metricas en tiempo real. Tomadas, cerradas, transferidas y cierres sin respuesta se registran automaticamente. Tiempo conectado se calcula por sesion. Alertas del auditor IA se actualizan cada 30 segundos.
      </div>

      <div className="report-grid two">
        <div className="ro-card">
          <div className="ro-card-header">
            <div><h2>Conversaciones con reloj corriendo</h2><p>Casos tomados por humano que siguen abiertos.</p></div>
            <span className={statusTagClass(data.reloj.length > 10 ? 'alto' : data.reloj.length > 0 ? 'medio' : 'ok')}>{data.reloj.length} pendientes</span>
          </div>
          <div className="ro-table-wrap">
            <table className="ro-table">
              <thead><tr><th>Cliente</th><th>Area</th><th>Agente</th><th>Abierta</th><th>Proxima accion</th><th>Estado</th></tr></thead>
              <tbody>
                {data.reloj.slice(0, 12).map(({ conv, ms, area, agente, accion }) => (
                  <tr key={conv.id}>
                    <td>{conv.nombre_cliente || 'Cliente'}</td>
                    <td>{area}</td>
                    <td>{agente}</td>
                    <td className={ms > 2 * 60 * 60 * 1000 ? 'bad' : 'warn'}>{formatDuration(ms)}</td>
                    <td>{accion}</td>
                    <td><span className={statusTagClass(ms > 2 * 60 * 60 * 1000 ? 'vencido' : 'pendiente')}>{ms > 2 * 60 * 60 * 1000 ? 'Vencido' : 'Pendiente'}</span></td>
                  </tr>
                ))}
                {data.reloj.length === 0 && <tr><td colSpan="6" className="ro-empty">No hay conversaciones humanas pendientes en este periodo.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ro-card">
          <div className="ro-card-header"><div><h2>Abiertas por area</h2><p>Donde se acumula trabajo sin cierre.</p></div></div>
          <div className="ro-bars">
            {data.byArea.map((x) => <Bar key={x.area} label={x.area} value={x.abiertas} max={data.maxArea} tone={x.area === 'ventas' ? 'green' : x.area === 'atencion' ? 'blue' : 'amber'} />)}
            <Bar label="Sin asignar" value={data.sinAsignar.length} max={data.maxArea} tone="red" />
          </div>
        </div>
      </div>

      <div className="report-section-title">
        <h2>Productividad y control por agente</h2>
        <span className="ro-tag blue">Tiempo operativo</span>
      </div>

      <div className="report-grid three">
        {data.agentesReporte.slice(0, 12).map((agente) => {
          const eficiencia = agente.tomadas > 0 ? Math.round((agente.cerradas / agente.tomadas) * 100) : 0;
          const maxReloj = Math.max(60, Math.round(Math.max(...data.agentesReporte.map((a) => a.relojMs)) / 60000));
          return (
            <div className="ro-card agent-card" key={agente.id}>
              <div className="agent-top"><div className="agent-avatar" style={agente.online ? { boxShadow: '0 0 0 3px #22c55e' } : {}}>{String(agente.nombre || 'A')[0]}</div><div><strong>{agente.nombre}</strong><span>{agente.online ? 'En linea' : 'Desconectado'}{agente.tiempoOnlineMin > 0 ? ` · ${formatDuration(agente.tiempoOnlineMin * 60000)} hoy` : ''}</span></div></div>
              <div className="agent-minis">
                <div><span>Tomadas</span><strong>{agente.tomadas}</strong></div>
                <div><span>Cerradas</span><strong className="good">{agente.cerradas}</strong></div>
                <div><span>Abiertas</span><strong className={agente.abiertas > 5 ? 'bad' : 'warn'}>{agente.abiertas}</strong></div>
                <div><span>Sin respuesta</span><strong className={agente.cerradasSinRespuesta > 0 ? 'bad' : ''}>{agente.cerradasSinRespuesta}</strong></div>
              </div>
              <Bar label="Eficiencia" value={eficiencia} max={100} tone={eficiencia > 75 ? 'green' : eficiencia > 55 ? 'amber' : 'red'} />
              <Bar label="Tiempo conectado" value={agente.tiempoOnlineMin} max={Math.max(480, Math.max(...data.agentesReporte.map((a) => a.tiempoOnlineMin)))} tone="blue" />
              <Bar label="Reloj abierto" value={Math.round(agente.relojMs / 60000)} max={maxReloj} tone="amber" />
              {agente.cerradasSinRespuesta > 0 && <div style={{ marginTop: 6, padding: '4px 8px', background: 'rgba(239,68,68,0.15)', borderRadius: 6, fontSize: 11, color: '#fca5a5' }}>⚠ {agente.cerradasSinRespuesta} cierre{agente.cerradasSinRespuesta > 1 ? 's' : ''} sin responder al cliente</div>}
            </div>
          );
        })}
        {data.agentesReporte.length === 0 && <div className="ro-card ro-empty-card">No hay actividad de agentes en este periodo.</div>}
      </div>

      <div className="report-grid two">
        <div className="ro-card">
          <div className="ro-card-header"><div><h2>Cierre y clasificacion IA</h2><p>Resoluciones automaticas y derivaciones por area.</p></div></div>
          <div className="ro-bars">
            {data.byArea.map((x) => <Bar key={x.area} label={`IA ${x.area}`} value={x.ia} max={Math.max(1, data.iaCerradas.length)} tone="green" />)}
            <Bar label="Humano cerrado" value={data.humanoCerradas.length} max={Math.max(1, data.cerradas.length)} tone="blue" />
          </div>
        </div>

        <div className="ro-card">
          <div className="ro-card-header"><div><h2>Alertas de organizacion</h2><p>Donde ajustar supervision, SLA o capacitacion.</p></div></div>
          <div className="ro-alerts">
            {data.sinAsignar.length > 0 && <div><span className="ro-tag red">Alto</span><strong>{data.sinAsignar.length} conversaciones sin asignar</strong><small>Asignar responsable o automatizar derivacion.</small></div>}
            {data.reloj.filter((x) => x.ms > 2 * 60 * 60 * 1000).length > 0 && <div><span className="ro-tag red">Alto</span><strong>{data.reloj.filter((x) => x.ms > 2 * 60 * 60 * 1000).length} casos con mas de 2 horas</strong><small>Definir cierre obligatorio o alerta al supervisor.</small></div>}
            {data.agentesReporte.some((a) => a.cerradasSinRespuesta > 0) && <div><span className="ro-tag red">Critico</span><strong>{data.agentesReporte.reduce((sum, a) => sum + a.cerradasSinRespuesta, 0)} cierres sin respuesta al cliente</strong><small>Agentes cerraron conversaciones sin enviar ningun mensaje. Requiere revision inmediata.</small></div>}
            {data.agentesReporte.some((a) => a.abiertas > 5) && <div><span className="ro-tag amber">Medio</span><strong>Agentes con backlog alto</strong><small>Revisar distribucion de carga y criterio de cierre.</small></div>}
            {data.iaCerradas.length > data.humanoCerradas.length && <div><span className="ro-tag green">IA</span><strong>IA resolviendo mas que humanos</strong><small>Buen indicador, revisar calidad de cierres y re-aperturas.</small></div>}
            {data.sinAsignar.length === 0 && data.reloj.length === 0 && !data.agentesReporte.some((a) => a.cerradasSinRespuesta > 0) && <div><span className="ro-tag green">OK</span><strong>Operacion sin alertas criticas</strong><small>No hay backlog humano relevante en el periodo.</small></div>}
          </div>
        </div>
      </div>

      {alertasIA.length > 0 && (() => {
        const alertasHumano = alertasIA.filter((a) => (a.responsable || a.metadata?.responsable) === 'humano');
        const alertasIAResp = alertasIA.filter((a) => (a.responsable || a.metadata?.responsable) === 'ia');
        const alertasSistema = alertasIA.filter((a) => (a.responsable || a.metadata?.responsable) === 'sistema');
        const alertasSinResp = alertasIA.filter((a) => !['humano', 'ia', 'sistema'].includes(a.responsable || a.metadata?.responsable));
        // Las que no tienen responsable se muestran con humano
        const humanoTotal = [...alertasHumano, ...alertasSinResp];

        const renderTabla = (alertas, titulo, subtitulo, emptyMsg) => (
          <div className="ro-card">
            <div className="ro-card-header">
              <div><h2>{titulo}</h2><p>{subtitulo}</p></div>
              <span className={statusTagClass(alertas.length > 0 ? 'alto' : 'ok')}>{alertas.length}</span>
            </div>
            <div className="ro-table-wrap">
              <table className="ro-table">
                <thead><tr><th>Tipo</th><th>Area</th><th>Prioridad</th><th>Descripcion</th><th>Accion sugerida</th><th>Ver</th></tr></thead>
                <tbody>
                  {alertas.slice(0, 15).map((a, i) => (
                    <tr key={a.id || i}>
                      <td style={{ minWidth: 140 }}>{a.titulo || a.tipo_alerta}</td>
                      <td>{a.area || '-'}</td>
                      <td><span className={statusTagClass(a.prioridad === 'critica' ? 'alto' : a.prioridad === 'alta' ? 'medio' : 'ok')}>{a.prioridad}</span></td>
                      <td style={{ fontSize: 12, lineHeight: 1.4 }}>{a.descripcion || '-'}</td>
                      <td style={{ fontSize: 12, lineHeight: 1.4 }}>{a.accion_sugerida || '-'}</td>
                      <td>{a.conversacion_id && onIrAConversacion ? <button style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }} onClick={() => onIrAConversacion(a.conversacion_id)} title="Ir a la conversacion">💬 Ver</button> : '-'}</td>
                    </tr>
                  ))}
                  {alertas.length === 0 && <tr><td colSpan="6" className="ro-empty">{emptyMsg}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );

        return (
          <>
            <div className="report-section-title">
              <h2>Alertas del Auditor IA</h2>
              <span className="ro-tag red">{alertasIA.length} pendientes</span>
            </div>

            <div className="report-grid three" style={{ marginBottom: 16 }}>
              <div className="ro-card ro-kpi"><span>Agente humano</span><strong className={humanoTotal.length > 0 ? 'bad' : ''}>{humanoTotal.length}</strong><small>Capacitacion o supervision</small></div>
              <div className="ro-card ro-kpi"><span>IA (prompts/flujos)</span><strong className={alertasIAResp.length > 0 ? 'warn' : ''}>{alertasIAResp.length}</strong><small>Ajustar prompts o logica IA</small></div>
              <div className="ro-card ro-kpi"><span>Sistema (codigo/config)</span><strong className={alertasSistema.length > 0 ? 'warn' : ''}>{alertasSistema.length}</strong><small>Corregir en codigo o config</small></div>
            </div>

            {humanoTotal.length > 0 && renderTabla(humanoTotal, 'Problemas del agente humano', 'El agente no hizo su trabajo: no respondio, cerro sin resolver, no derivo.', 'Sin alertas de agentes.')}

            {alertasIAResp.length > 0 && renderTabla(alertasIAResp, 'Problemas de la IA', 'La IA cometio un error: loop, no escalo, expuso datos, cerro cuando no debia.', 'Sin alertas de IA.')}

            {alertasSistema.length > 0 && renderTabla(alertasSistema, 'Problemas del sistema', 'Falla tecnica o de configuracion: derivacion rota, Telegram no enviado, credenciales expuestas.', 'Sin alertas de sistema.')}

            <div className="report-grid two">
              <div className="ro-card">
                <div className="ro-card-header"><div><h2>Por prioridad</h2><p>Severidad de las alertas pendientes.</p></div></div>
                <div className="ro-bars">
                  <Bar label="Criticas" value={alertasIA.filter((a) => a.prioridad === 'critica').length} max={Math.max(1, alertasIA.length)} tone="red" />
                  <Bar label="Altas" value={alertasIA.filter((a) => a.prioridad === 'alta').length} max={Math.max(1, alertasIA.length)} tone="amber" />
                  <Bar label="Medias" value={alertasIA.filter((a) => a.prioridad === 'media').length} max={Math.max(1, alertasIA.length)} tone="blue" />
                  <Bar label="Bajas" value={alertasIA.filter((a) => a.prioridad === 'baja').length} max={Math.max(1, alertasIA.length)} tone="green" />
                </div>
              </div>

              <div className="ro-card">
                <div className="ro-card-header"><div><h2>Por tipo de problema</h2><p>Que esta fallando mas.</p></div></div>
                <div className="ro-bars">
                  <Bar label="Cierres sin respuesta" value={alertasIA.filter((a) => a.tipo_alerta === 'cierre_sin_respuesta_humana').length} max={Math.max(1, alertasIA.length)} tone="red" />
                  <Bar label="Msg cliente pendiente" value={alertasIA.filter((a) => a.tipo_alerta === 'cierre_con_mensaje_cliente_pendiente').length} max={Math.max(1, alertasIA.length)} tone="amber" />
                  <Bar label="Humano sin cierre" value={alertasIA.filter((a) => a.tipo_alerta === 'humano_sin_cierre').length} max={Math.max(1, alertasIA.length)} tone="blue" />
                  <Bar label="Venta caliente" value={alertasIA.filter((a) => a.tipo_alerta === 'venta_caliente_sin_confirmar').length} max={Math.max(1, alertasIA.length)} tone="green" />
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </section>
  );
}
